import { SingleAndMultiSelectFieldOption } from 'module/api/field/types';
import { useTranslate } from 'module/hooks/use-translation';
import { withAiStyles } from 'module/ui/components/atoms/ai/background-hoc';
import { AiIndicator } from 'module/ui/components/atoms/ai/button';
import { Form } from 'module/ui/components/atoms/form';
import { StyledItem } from 'module/ui/components/atoms/form/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Input } from 'module/ui/components/atoms/input/input';
import {
  Short00,
  Short01,
  SingleLineEllipsis,
} from 'module/ui/components/styles';
import {
  closestCenter,
  defaultDropAnimation,
  DndContext,
  DragOverlay,
  DropAnimation,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  useSortable,
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { Button, Textarea } from '@rocketlane/ui';
import { FormInstance } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const DragHandle = styled.div`
  color: var(--icon-secondary);
  width: 24px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;
const ActiveDragHandle = styled(DragHandle)`
  cursor: grabbing;
`;
const FieldLabel = styled.div`
  ${Short01};
  ${SingleLineEllipsis};
  cursor: pointer;
`;
const AddDescriptionAction = styled(Button).attrs({
  variant: 'ghost',
  type: 'button',
  size: 'small',
})`
  ${Short00};
`;
const CloseIcon = styled(Button).attrs({
  variant: 'ghost',
  type: 'button',
  size: 'medium',
  leftIcon: <FontAwesomeIcon icon={faClose} />,
})``;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

export const BaseFieldContent = styled.div`
  display: flex;
  align-items: center;
  border-radius: var(--border-radius-default);
  border: 1px solid var(--field-border-default);
  padding: var(--spacing-02) var(--spacing-05);
  width: calc(calc(100% - 24px) - var(--height-base));
  justify-content: space-between;
  ${Actions} {
    flex-shrink: 0;
  }
`;
const FieldContent = withAiStyles(BaseFieldContent);
const FieldRow = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;

  ${StyledItem} {
    width: 100%;

    .ant-form-item-control-input-content {
      display: flex;
    }
  }
`;

export const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  ${CloseIcon},
  ${DragHandle} {
    flex-shrink: 0;
  }
`;
const defaultDropAnimationConfig: DropAnimation = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.5,
};

interface OptionProps {
  optionsManagedByAi?: boolean;
  option: SingleAndMultiSelectFieldOption;
  removeOption: VoidFunction;
  editOption?: VoidFunction;
  editInputOption?: (updatedOption: SingleAndMultiSelectFieldOption) => void; // Update type
  shouldDrag?: boolean;
  onAddOptionInputBlur?: (name: string) => void;
  form?: FormInstance<any>;
  validateForm?: (formData: any) => void;
  showOrderedChoices?: boolean;
  labelText?: React.ReactNode;
  index?: number;
  inputType?: 'textarea' | 'text';
  canDeleteChoices?: boolean;
}

const StyledTextArea = styled(Textarea)`
  width: 100%;
`;

const FormOption: FC<OptionProps> = ({
  option,
  removeOption,
  editInputOption,
  shouldDrag,
  onAddOptionInputBlur,
  form,
  index,
  showOrderedChoices,
  labelText,
  inputType = 'text',
  canDeleteChoices = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: option.value.toString(),
    disabled: !shouldDrag,
  });
  const translate = useTranslate();

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    border: isDragging ? '2px dashed var(--blue-70)' : 'none',
  };

  const requiredMessage = translate('input.required');

  return (
    <FieldRow ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {shouldDrag ? (
        <DragHandle>
          <FontAwesomeIcon size={'1x'} icon={faGripDotsVertical} />
        </DragHandle>
      ) : null}

      <Form.Item
        name={option.value.toString()}
        rules={[{ required: true, message: requiredMessage }]}
        label={
          showOrderedChoices
            ? `${(index ?? 0) + 1}. ${labelText?.toString()}`
            : labelText
        }
      >
        {inputType === 'textarea' ? (
          <StyledTextArea
            id={option.value.toString()}
            rows={1}
            maxRows={3}
            onChange={(e) => {
              if (form) {
                form.setFieldsValue({
                  [option.value]: e.target.value,
                });

                form
                  .validateFields([option.value.toString()])
                  .catch((error) => {
                    form.setFields([
                      {
                        name: option.value.toString(),
                        errors: error.errorFields[0].errors,
                      },
                    ]);
                  });
              }

              if (editInputOption) {
                editInputOption({ ...option, label: e.target.value });
              }
            }}
            onBlur={() => onAddOptionInputBlur?.(option.value.toString())}
            value={option.label}
            autoFocus
            // onKeyDown={onAddOptionInputKeyDown}
          />
        ) : (
          <Input
            id={option.value.toString()}
            onChange={(e) => {
              if (form) {
                form.setFieldsValue({
                  [option.value]: e.target.value,
                });

                form
                  .validateFields([option.value.toString()])
                  .catch((error) => {
                    form.setFields([
                      {
                        name: option.value.toString(),
                        errors: error.errorFields[0].errors,
                      },
                    ]);
                  });
              }

              if (editInputOption) {
                editInputOption({ ...option, label: e.target.value });
              }
            }}
            onBlur={() => onAddOptionInputBlur?.(option.value.toString())}
            value={option.label}
            autoFocus
            // onKeyDown={onAddOptionInputKeyDown}
          />
        )}
        {canDeleteChoices ? <CloseIcon onClick={removeOption} /> : null}
      </Form.Item>
    </FieldRow>
  );
};

const Option: FC<OptionProps> = ({
  option,
  removeOption,
  editOption,
  editInputOption,
  optionsManagedByAi,
  shouldDrag,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: option.value.toString(),
    disabled: !shouldDrag,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    border: isDragging ? '2px dashed var(--blue-70)' : 'none',
  };

  return (
    <FieldRow ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {shouldDrag ? (
        <DragHandle>
          <FontAwesomeIcon size={'1x'} icon={faGripDotsVertical} />
        </DragHandle>
      ) : null}
      {optionsManagedByAi ? (
        <>
          <FieldContent
            aiGenerated={
              option.fieldOptionMeta?.isAiGenerated &&
              option.fieldOptionMeta?.isDescriptionAiGenerated
            }
          >
            <FieldLabel onClick={editOption}>{option.label}</FieldLabel>
            {optionsManagedByAi ? (
              <Actions>
                <AddDescriptionAction onClick={editOption}>
                  {!option.fieldOptionMeta?.isDescriptionAiGenerated ? (
                    <FormattedMessage id="edited_instructions" />
                  ) : (
                    <FormattedMessage id="instructions_to_ai" />
                  )}
                </AddDescriptionAction>
                <AiIndicator
                  aiGenerated={
                    !!(
                      option.fieldOptionMeta?.isAiGenerated &&
                      option.fieldOptionMeta?.isDescriptionAiGenerated
                    )
                  }
                  size="xxs"
                />
              </Actions>
            ) : null}
          </FieldContent>
          <CloseIcon onClick={removeOption} />
        </>
      ) : (
        <>
          <Input
            id="title"
            type="text"
            onChange={(e) =>
              editInputOption &&
              editInputOption({ ...option, label: e.target.value })
            }
            value={option.label}
            autoFocus
          />
          <CloseIcon onClick={removeOption} />
        </>
      )}
    </FieldRow>
  );
};

interface OptionListProps {
  optionsManagedByAi?: boolean;
  options: SingleAndMultiSelectFieldOption[];
  removeOption: (option: SingleAndMultiSelectFieldOption) => void;
  updateOptions: (options: SingleAndMultiSelectFieldOption[]) => void;
  editOption?: (option: SingleAndMultiSelectFieldOption) => void;
  editInputOption?: (updatedOption: SingleAndMultiSelectFieldOption) => void;
  shouldDrag?: boolean;
  validateForm?: (formData: any) => void;
  labelText?: React.ReactNode;
  showOrderedChoices?: boolean;
  inputType?: 'textarea' | 'text';
  canDeleteChoices?: boolean;
}
export const SortableOptions: FC<OptionListProps> = ({
  labelText,
  showOrderedChoices = false,
  optionsManagedByAi = false,
  options,
  removeOption,
  updateOptions,
  editOption,
  editInputOption,
  canDeleteChoices = true,
  shouldDrag = true,
  validateForm,
  inputType = 'text',
}) => {
  const [form] = Form.useForm();

  const optionsWithIds = useMemo(
    () => options.map((option) => ({ ...option, id: option.value.toString() })),
    [options],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );
  const [draggedOption, setDraggedOption] = useState<any>();
  const handleDragStart = useCallback(
    (event) => {
      const targetedOption = options.find(
        (option) => option.value.toString() === event.active.id,
      );

      setDraggedOption(targetedOption);
    },
    [options],
  );
  const handleDragCancel = useCallback(() => {
    setDraggedOption(null);
  }, []);
  const handleDragEnd = useCallback(
    ({ over }) => {
      if (over) {
        const activeIndex = options.findIndex(
          (option) => option.value.toString() === draggedOption.value,
        );
        const overIndex = options.findIndex(
          (option) => option.value.toString() === over.id,
        );
        const modifiedOptions = arrayMove(options, activeIndex, overIndex);

        updateOptions(
          modifiedOptions.map((option, index) => ({
            ...option,
            position: index + 1,
          })),
        );
      }

      setDraggedOption(null);
    },
    [draggedOption, options, updateOptions],
  );

  const onAddOptionInputBlur = useCallback(
    (name: string) => {
      const value = form.getFieldValue(name);

      if (value) {
        form.setFields([
          {
            name,
            errors: [],
          },
        ]);
      }
    },
    [form],
  );

  return (
    <FieldsWrapper>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext strategy={rectSortingStrategy} items={optionsWithIds}>
          {optionsManagedByAi ? (
            <>
              {options.map((option) => (
                <Option
                  key={option.value}
                  option={option}
                  removeOption={() => removeOption(option)}
                  editOption={editOption ? () => editOption(option) : undefined}
                  optionsManagedByAi={optionsManagedByAi}
                  shouldDrag={shouldDrag}
                />
              ))}
            </>
          ) : (
            <>
              <Form
                form={form}
                layout="vertical"
                onFieldsChange={() => {
                  validateForm?.(form);
                }}
              >
                {options.map((option, index) => (
                  <FormOption
                    showOrderedChoices={showOrderedChoices}
                    labelText={labelText}
                    key={option.value.toString()}
                    index={index}
                    inputType={inputType}
                    option={option}
                    canDeleteChoices={canDeleteChoices}
                    removeOption={() => {
                      removeOption(option);
                      form.setFields([
                        {
                          name: option.value.toString(),
                          errors: [],
                        },
                      ]);
                      validateForm?.(form);
                    }}
                    editOption={
                      editOption ? () => editOption(option) : undefined
                    }
                    editInputOption={editInputOption}
                    optionsManagedByAi={optionsManagedByAi}
                    shouldDrag={shouldDrag}
                    onAddOptionInputBlur={onAddOptionInputBlur}
                    form={form}
                    validateForm={validateForm}
                  />
                ))}
              </Form>
            </>
          )}
        </SortableContext>
        {createPortal(
          <DragOverlay
            adjustScale={false}
            dropAnimation={defaultDropAnimationConfig}
            style={{ zIndex: 1040, background: 'var(--white-100)' }}
          >
            {draggedOption ? (
              <FieldRow>
                <ActiveDragHandle>
                  <FontAwesomeIcon size={'1x'} icon={faGripDotsVertical} />
                </ActiveDragHandle>
                <FieldContent aiGenerated={draggedOption.aiGenerated}>
                  <FieldLabel>{draggedOption.label}</FieldLabel>
                  {optionsManagedByAi ? (
                    <Actions>
                      <AddDescriptionAction>
                        <FormattedMessage id="instructions_to_ai" />
                      </AddDescriptionAction>
                      <AiIndicator aiGenerated />
                    </Actions>
                  ) : null}
                </FieldContent>
                <CloseIcon />
              </FieldRow>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </FieldsWrapper>
  );
};
